/**
 * STRICT: Database detection with explicit mention requirements for Utdanning.no
 */
import { UTDANNING_SEMANTIC_CATEGORIES } from '../services/utdanning-semantic-mapper';

/**
 * Job-related terms for detection
 */
const JOB_TERMS = [
  'jobb', 'jobber', 'stilling', 'stillinger', 'ledige stillinger', 'ledig stilling',
  'ansette', 'ansettelse', 'rekruttering', 'karriere', 'arbeid', 'arbeidsplasser',
  'søke jobb', 'jobbsøking', 'jobbsøker', 'deltid', 'heltid', 'fast ansettelse',
  'midlertidig ansettelse', 'vikariat', 'sommerjobb', 'ekstrajobb'
];

/**
 * Enhanced business/company terms for BRREG detection
 */
const BUSINESS_TERMS = [
  // Explicit business registry terms (highest priority)
  'enhetsregister', 'enhetsregisteret', 'brønnøysund', 'brreg',
  
  // Core business terms
  'bedrift', 'bedrifter', 'selskap', 'selskaper', 'firma', 'firmaer', 
  'organisasjon', 'organisasjoner', 'virksomhet', 'virksomheter', 'foretak',
  'aksjeselskap', 'orgnr', 'organisasjonsnummer',
  'næring', 'bransje', 'næringsvirksomhet', 'foretaksregister',
  
  // Enhanced construction trades
  'snekker', 'snekkere', 'snekkerbedrift', 'snekkerbedrifter',
  'tømrer', 'tømrere', 'tømrerbedrift', 'tømrerbedrifter', 
  'byggmester', 'byggmestere', 'byggmesterbedrift',
  'rørlegger', 'rørleggere', 'rørleggerbedrift', 'rørleggerbedrifter',
  'elektriker', 'elektrikere', 'elektrikerbedrift', 'elektrikerbedrifter',
  'maler', 'malere', 'malerbedrift', 'malerbedrifter',
  'murer', 'murere', 'murerbedrift',
  
  // Service trades
  'restaurant', 'restauranter', 'café', 'kafé', 'kaféer', 'kafe', 'kafeer',
  'bar', 'pub', 'bakeri', 'konditori',
  'butikk', 'butikker', 'forretning', 'forretninger', 'handel', 'dagligvare', 'supermarked',
  'frisør', 'frisører', 'skjønnhetssalong', 'spa', 'massasje', 
  'tannlege', 'lege', 'legekontor',
  'bilverksted', 'bilverksteder', 'bilforhandler', 'bilforhandlere', 'bilselger',
  
  // Professional services
  'regnskapsfører', 'regnskapsførere', 'advokat', 'advokater',
  'eiendomsmegler', 'eiendomsmeglere', 'konsulent', 'konsulenter',
  'bank', 'forsikring', 'revisjon'
];

/**
 * STRICT: Education-specific terms (requires explicit Utdanning.no mention)
 */
const EDUCATION_TERMS = [
  // REMOVED general terms that don't require explicit mention
  'utdanning.no', 'utdanningno', 'utdanning no'
  // Note: General terms like 'utdanning', 'studie', 'bachelor' etc. removed
  // to require explicit Utdanning.no mention
];

/**
 * Complete Norwegian municipalities list for location detection
 */
const NORWEGIAN_MUNICIPALITIES = [
  // Oslo
  'oslo',
  
  // Major cities - Include both ø and o variants
  'bergen', 'trondheim', 'stavanger', 'tromsø', 'tromso', 
  'bodø', 'bodo', 'ålesund', 'alesund', 'kristiansand', 'drammen',
  
  // More municipalities
  'florø', 'floro', 'sogndal', 'førde', 'forde', 'voss', 'oppdal', 'røros', 'roros',
  'lillehammer', 'gjøvik', 'hamar', 'kongsberg', 'fredrikstad', 'sarpsborg',
  'skien', 'porsgrunn', 'sandefjord', 'tønsberg', 'larvik', 'moss', 'arendal',
  'grimstad', 'risør', 'flekkefjord', 'mandal', 'harstad', 'narvik', 'alta',
  'hammerfest', 'kirkenes', 'vadsø', 'vadso', 'bardufoss', 'sortland'
];

/**
 * Action verbs that indicate a search query
 */
const ACTION_VERBS = [
  'finn', 'finne', 'vis', 'vise', 'list', 'liste', 'gi meg', 'hent', 'hente',
  'hvilke', 'hvor', 'hvem', 'hva', 'er det', 'søk', 'søke',
  'kan du finne', 'kan du søke', 'kan du vise', 'kan du hente'
];

/**
 * STRICT: Enhanced database detection with explicit mention requirements
 */
export const detectDatabaseFromQuery = (query: string): string => {
  const lowerQuery = query.toLowerCase();
  
  console.log(`[STRICT-ROUTING] Database detection for query: "${lowerQuery}"`);
  
  // HIGHEST PRIORITY - Explicit Enhetsregister mentions
  if (lowerQuery.includes('enhetsregister') || lowerQuery.includes('enhetsregisteret') ||
      lowerQuery.includes('brønnøysund') || lowerQuery.includes('brreg')) {
    console.log('[STRICT-ROUTING] ✅ EXPLICIT ENHETSREGISTER mention detected, routing to BRREG');
    return 'brreg';
  }
  
  // SECOND HIGHEST - Explicit Arbeidsplassen.no mentions
  if (lowerQuery.includes('arbeidsplassen.no') || 
      lowerQuery.includes('arbeidsplassen') ||
      lowerQuery.includes('nav.no') ||
      (lowerQuery.includes('nav') && (lowerQuery.includes('jobb') || lowerQuery.includes('stilling')))) {
    console.log('[STRICT-ROUTING] ✅ EXPLICIT ARBEIDSPLASSEN.NO mention detected, routing to arbeidsplassen');
    return 'arbeidsplassen';
  }
  
  // THIRD PRIORITY - STRICT Utdanning.no mentions (ONLY explicit)
  if (lowerQuery.includes('utdanning.no') || lowerQuery.includes('utdanningno') || lowerQuery.includes('utdanning no')) {
    console.log('[STRICT-ROUTING] ✅ EXPLICIT UTDANNING.NO mention detected, routing to utdanning');
    return 'utdanning';
  }
  
  // Check for business terms and locations
  const hasBusinessTerms = BUSINESS_TERMS.some(term => lowerQuery.includes(term));
  const hasLocation = NORWEGIAN_MUNICIPALITIES.some(municipality => lowerQuery.includes(municipality));
  const hasActionVerb = ACTION_VERBS.some(verb => lowerQuery.includes(verb));
  const hasJobTerms = JOB_TERMS.some(term => lowerQuery.includes(term));
  
  console.log(`[STRICT-ROUTING] Term analysis:`, {
    hasBusinessTerms,
    hasLocation,
    hasActionVerb,
    hasJobTerms,
    explicitUtdanningNoMention: false // General education terms no longer count
  });
  
  // Business + Location pattern (handles "snekkere i Tromsø")
  if (hasBusinessTerms && hasLocation) {
    console.log(`[STRICT-ROUTING] ✅ Business + Location pattern detected -> routing to BRREG`);
    return 'brreg';
  }
  
  // Business terms with search intent
  if (hasBusinessTerms && hasActionVerb) {
    console.log(`[STRICT-ROUTING] ✅ Business + Action pattern detected -> routing to BRREG`);
    return 'brreg';
  }
  
  // Job-related terms with action verbs (only if no business context)
  if (hasJobTerms && hasActionVerb && !hasBusinessTerms) {
    console.log('[STRICT-ROUTING] ✅ Job terms + action verb detected (no business context), routing to arbeidsplassen');
    return 'arbeidsplassen';
  }
  
  // Location-based searches with action verbs (default to business search)
  if ((hasLocation) && hasActionVerb) {
    console.log('[STRICT-ROUTING] ✅ Location + action verb detected, defaulting to brreg for business search');
    return 'brreg';
  }
  
  // Statistics-related queries
  if (lowerQuery.includes('statistic') || 
      lowerQuery.includes('statistikk') || 
      lowerQuery.includes('ssb') || 
      lowerQuery.includes('norge') || 
      lowerQuery.includes('befolkning')) {
    console.log('[STRICT-ROUTING] ✅ Statistics-related query, routing to ssb');
    return 'ssb';
  }
  
  // Default behavior
  console.log('[STRICT-ROUTING] ❓ No specific database detected, defaulting to brreg');
  return 'brreg';
};

/**
 * Check if a query is specifically about jobs
 */
export const isJobQuery = (query: string): boolean => {
  const lowerQuery = query.toLowerCase();
  
  return JOB_TERMS.some(term => lowerQuery.includes(term)) ||
         (lowerQuery.includes('finn') && 
          NORWEGIAN_MUNICIPALITIES.some(municipality => lowerQuery.includes(municipality)) &&
          !lowerQuery.includes('utdanning') && !lowerQuery.includes('bedrift'));
};

export const isBusinessQuery = (query: string): boolean => {
  const lowerQuery = query.toLowerCase();
  
  return BUSINESS_TERMS.some(term => lowerQuery.includes(term)) ||
         lowerQuery.match(/\b(bedrifter?|selskaper?|firmaer?|snekkerbedrifter?)\s+(i|fra|ved|nær)\s+\w+/) !== null ||
         lowerQuery.includes('org.nr') ||
         lowerQuery.includes('organisasjonsnummer') ||
         /\b(snekker|rørlegger|elektriker|tømrer|byggmester)\s+(i|fra|ved)\s+(\w+)/i.test(lowerQuery);
};

/**
 * STRICT: Education query detection - ONLY explicit Utdanning.no mentions
 */
export const isEducationQuery = (query: string): boolean => {
  const lowerQuery = query.toLowerCase();
  
  // STRICT: Only explicit Utdanning.no mentions count
  return lowerQuery.includes('utdanning.no') || 
         lowerQuery.includes('utdanningno') || 
         lowerQuery.includes('utdanning no');
};

export const mentionsSupportedMunicipality = (query: string): boolean => {
  const lowerQuery = query.toLowerCase();
  return NORWEGIAN_MUNICIPALITIES.some(municipality => lowerQuery.includes(municipality));
};
